import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { IconCheck } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import {
  MilestonePhaseItemDto,
  OrganizationRoleDto,
} from 'src/api/FiretrolApiClient';
import { CopyActionIcon } from 'src/design/atoms/CopyActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { MilestoneMoveActionIcons } from 'src/app/milestones/MilestoneMoveActionIcons';
import {
  MILESTONE_ITEM_DEFAULT_SORTING,
  milestoneItemOrderColumn,
  milestonePhaseOrderSortingFn,
} from 'src/app/milestones/milestoneItemOrder';
import { useMilestoneItemMove } from 'src/app/milestones/useMilestoneItemMove';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (
  hideStatus: boolean,
): MRT_ColumnDef<MilestonePhaseItemDto>[] => {
  const baseColumns: MRT_ColumnDef<MilestonePhaseItemDto>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      size: 400,
    },
    {
      id: 'critical',
      accessorFn: (row) => (row.critical === 'true' ? 'Yes' : 'No'),
      header: 'Critical',
    },
    {
      id: 'complete',
      accessorFn: (row) => (row.complete ? 'Yes' : 'No'),
      header: 'Complete',
    },
    {
      id: 'requireAssociation',
      accessorFn: (row) => (row.requireAssociation === 'true' ? 'Yes' : 'No'),
      header: 'Driver',
    },
    {
      id: 'phase',
      accessorFn: (row) => row.phase?.title || '',
      header: 'Phase',
      size: 150,
    },
    {
      id: 'assignedTo',
      accessorFn: (row) => row.assignedTo,
      header: 'Assigned To',
      size: 150,
      Cell: ({ cell }) => {
        const assignedTo = cell.getValue<OrganizationRoleDto[]>();
        return (
          <Group gap="xs">
            {assignedTo.map((role: OrganizationRoleDto) => (
              <Badge key={role.id} variant="light" size="sm">
                {role.name}
              </Badge>
            ))}
          </Group>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      id: 'phase.order',
      header: 'Phase Order',
      accessorFn: (row) => row.phase?.order,
      sortingFn: milestonePhaseOrderSortingFn,
      enableHiding: false,
      visibleInShowHideMenu: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    milestoneItemOrderColumn,
  ];

  if (hideStatus) {
    return baseColumns;
  }

  return [
    {
      id: 'status',
      header: '',
      size: 25,
      Cell: ({ cell }) => {
        const item = cell.row.original;
        return item.complete ? (
          <IconCheck color="green" style={{ marginLeft: 5 }} />
        ) : (
          <IconCheck style={{ marginLeft: 5 }} />
        );
      },
    },
    ...baseColumns,
  ];
};

interface IMilestonePhaseItemTableProps {
  templateId: string;
  data: MilestonePhaseItemDto[];
  onRowClick: (item: MilestonePhaseItemDto) => void;
  onCopyClick: (item: MilestonePhaseItemDto) => void;
  loading?: boolean;
  hideStatus?: boolean;
  onCreateClick?: () => void;
}

export const MilestonePhaseItemTable: React.FC<
  IMilestonePhaseItemTableProps
> = ({
  templateId,
  data,
  onRowClick,
  onCopyClick,
  hideStatus = false,
  loading = false,
  onCreateClick,
}) => {
  const columns = useMemo(() => getColumns(hideStatus), [hideStatus]);

  const tableState = useTableState({
    identifier: 'milestone-phase-items-ordered',
    columns: columns,
    defaultSorting: MILESTONE_ITEM_DEFAULT_SORTING,
  });

  const getMoveProps = useMilestoneItemMove({
    items: data,
    scope: { templateId },
    sorting: tableState.sorting,
    columnFilters: tableState.columnFilters,
    globalFilter: tableState.globalFilter,
  });

  const tableExports = useTableExports<MilestonePhaseItemDto>(
    'milestone-phase-items',
    (data) =>
      data.map((item) => ({
        ['Title']: item.title,
        ['Critical']: item.critical === 'true' ? 'Yes' : 'No',
        ['Complete']: item.complete ? 'Yes' : 'No',
        ['Driver']: item.requireAssociation === 'true' ? 'Yes' : 'No',
        ['Phase']: item.phase?.title || '',
        ['Assigned To']:
          item.assignedTo
            ?.map((r: OrganizationRoleDto) => r.name)
            .join(' | ') || '',
        ['Description']: item.description || '',
      })),
  );

  const { table } = useTable({
    ...tableState,
    columnVisibility: {
      ...tableState.columnVisibility,
      'phase.order': false,
      order: false,
    },
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 200 } },
      renderRowActions: ({ row }) => {
        const item = row.original;
        return (
          <Group wrap="nowrap">
            <EditActionIcon onClick={() => onRowClick(item)} />
            <CopyActionIcon onClick={() => onCopyClick(item)} />
            <MilestoneMoveActionIcons {...getMoveProps(item)} />
          </Group>
        );
      },
      renderTopToolbarCustomActions: () => {
        if (onCreateClick) {
          return <CreateButton onClick={onCreateClick}>Add Item</CreateButton>;
        }
        return null;
      },
      enableClickToCopy: false,
      columns: columns,
      data: data,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: data.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return <>{table}</>;
};
